import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, MessageSquare } from "lucide-react";
import { useBusinessMessaging } from "../context/BusinessMessagingContext";
import { useBusinessAuth } from "../context/BusinessAuthContext";
import MessageInput from "../../components/messaging/MessageInput";
import ConversationList from "../../components/messaging/ConversationList";
import MessageList from "../../components/messaging/MessageList";
import { getConversationDisplayName } from "../../components/messaging/messagingUi";

function toConversationKey(value) {
  if (value == null) return "";
  return String(value);
}

export default function BusinessMessages() {
  const {
    conversations,
    activeConversationId,
    activeConversation,
    messages,
    unreadTotal,
    activeBusinessId,
    error,
    loading,
    loadingConversations,
    loadingMessages,
    selectConversation,
    searchConversations,
    sendMessage,
    deleteMessage,
    openInbox,
    closeInbox,
    clearError,
    sendingMessage,
    deletingMessageId,
    getConversationUnreadCount,
  } = useBusinessMessaging();
  const { user } = useBusinessAuth();

  const [mobileView, setMobileView] = useState("list");
  const [interactionError, setInteractionError] = useState("");
  const activeConversationKey = toConversationKey(activeConversationId);

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => toConversationKey(conversation.id) === activeConversationKey) || activeConversation || null,
    [activeConversation, activeConversationKey, conversations]
  );

  const selectedConversationName = useMemo(
    () => getConversationDisplayName(selectedConversation, "SELLER"),
    [selectedConversation]
  );

  const currentBusinessUserId = Number(user?.id || 0);
  const hasActiveBusiness = Number.isInteger(Number(activeBusinessId)) && Number(activeBusinessId) > 0;
  const isInitialLoading = loading || (loadingConversations && conversations.length === 0);

  useEffect(() => {
    openInbox();

    return () => {
      closeInbox();
    };
  }, [activeBusinessId, closeInbox, openInbox]);

  useEffect(() => {
    if (!interactionError) return undefined;

    const timeoutId = window.setTimeout(() => {
      setInteractionError("");
    }, 6000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [interactionError]);

  useEffect(() => {
    if (!error) return;
    setInteractionError(error);
  }, [error]);

  useEffect(() => {
    if (activeConversationId) {
      setMobileView("thread");
      return;
    }

    setMobileView("list");
  }, [activeConversationId]);

  const handleSelectConversation = async (conversationId) => {
    setInteractionError("");
    clearError();
    await selectConversation(conversationId);
    setMobileView("thread");
  };

  const handleSendMessage = async (text, file) => {
    if (!activeConversationId || sendingMessage) return false;

    setInteractionError("");
    clearError();
    const result = await sendMessage(activeConversationId, text, file ?? null);
    if (!result) {
      setInteractionError("Unable to send message right now. Please try again.");
      return false;
    }
    return result;
  };

  const handleDeleteMessage = async (messageId) => {
    if (!activeConversationId || !messageId || deletingMessageId) return false;

    setInteractionError("");
    clearError();
    const deleted = await deleteMessage(activeConversationId, messageId);

    if (!deleted) {
      setInteractionError("Unable to delete that message. It may no longer be removable.");
      return false;
    }

    return true;
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {/* A full-height split inbox: the breadcrumb shows the location, this names the page. */}
      <h1 className="sr-only">Inbox</h1>
      <section className="flex h-full min-h-0 flex-1 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex h-full min-h-0 flex-1 flex-col md:flex-row">
          <aside className={`min-h-0 w-full flex-1 flex-col overflow-hidden border-b border-neutral-200 dark:border-neutral-800 md:w-[340px] md:shrink-0 md:flex-none md:border-b-0 md:border-r lg:w-[368px] ${mobileView === "thread" ? "hidden md:flex" : "flex"}`}>
            <div className="shrink-0 border-b border-neutral-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-neutral-800 dark:bg-neutral-900/95">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                    Conversations
                  </h2>
                  <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                    {hasActiveBusiness
                      ? (loadingConversations ? "Refreshing business inbox..." : `${conversations.length} conversation${conversations.length === 1 ? "" : "s"}`)
                      : "Select a business to load conversations."}
                  </p>
                </div>

                <div className="inline-flex items-center rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                  {unreadTotal} unread
                </div>
              </div>
            </div>

            <ConversationList
              conversations={conversations}
              activeConversationId={activeConversationId}
              loading={isInitialLoading}
              onSelect={handleSelectConversation}
              actorType="SELLER"
              getConversationUnreadCount={getConversationUnreadCount}
              onSearch={searchConversations}
              emptyDescription="Shopper messages will appear here once a conversation reaches this business."
              scrollRegionClassName="scrollbar-fade"
            />
          </aside>

          <div className={`min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${mobileView === "thread" ? "flex" : "hidden md:flex"}`}>
            <div className="shrink-0 border-b border-neutral-200 bg-white/95 px-3 py-3 backdrop-blur dark:border-neutral-800 dark:bg-neutral-900/95">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                <button
                  type="button"
                  onClick={() => setMobileView("list")}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-neutral-200 bg-white text-neutral-700 transition hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800 md:hidden"
                  aria-label="Back to conversations"
                >
                  <ArrowLeft size={16} />
                </button>

                {selectedConversation ? (
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                      {selectedConversationName}
                    </p>
                  </div>
                ) : (
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                      Messages
                    </p>
                  </div>
                )}
                </div>

              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {interactionError ? (
                <div className="hidden shrink-0 border-b border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-5 text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-200 md:block" role="status" aria-live="polite">
                  {interactionError}
                </div>
              ) : null}

              {!hasActiveBusiness ? (
                <div className="flex flex-1 flex-col items-center justify-center px-6 text-center text-neutral-500 dark:text-neutral-400">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800">
                    <MessageSquare size={24} />
                  </div>
                  <p className="mt-4 text-sm font-medium text-neutral-700 dark:text-neutral-200">
                    No active business selected
                  </p>
                  <p className="mt-1 max-w-sm text-xs leading-relaxed">
                    Seller messaging requires an active business. Select a business first to load the inbox.
                  </p>
                </div>
              ) : !selectedConversation && !isInitialLoading ? (
                <div className="flex flex-1 flex-col items-center justify-center px-6 text-center text-neutral-500 dark:text-neutral-400">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800">
                    <MessageSquare size={24} />
                  </div>
                  <p className="mt-4 text-sm font-medium text-neutral-700 dark:text-neutral-200">
                    No conversation selected
                  </p>
                  <p className="mt-1 max-w-sm text-xs leading-relaxed">
                    Choose a shopper conversation from the left pane to view the business thread.
                  </p>
                </div>
              ) : (
                <>
                  <MessageList
                    messages={messages}
                    conversationKey={activeConversationKey}
                    actorType="SELLER"
                    loading={Boolean(selectedConversation && loadingMessages)}
                    onDelete={handleDeleteMessage}
                    canDeleteMessage={(message) => (
                      message.sender_type === "SELLER" && Number(message.sender_id) === currentBusinessUserId
                    )}
                    pendingDeleteId={deletingMessageId}
                    emptyDescription="This conversation exists, but there are no messages to display yet."
                    loadingDescription="Fetching messages for this shopper conversation."
                    className="scrollbar-fade bg-neutral-50/70 dark:bg-neutral-950/20"
                  />

                  <div className="shrink-0 border-t border-neutral-200 bg-white/95 px-0 backdrop-blur dark:border-neutral-800 dark:bg-neutral-900/95">
                    <MessageInput
                      onSend={handleSendMessage}
                      sending={sendingMessage}
                      disabled={!hasActiveBusiness || !selectedConversation || loadingMessages || deletingMessageId != null}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
